import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import Hearts from 'stile-shared/src/components/2024/common/Hearts';
import ReviewsSlider from 'stile-shared/src/components/2024/layout/ReviewsSlider/ReviewsSlider';

import { PageLayout } from 'templates/2024/PageLayout';

import AlignedSection from 'components/blocks/states/AlignedSection';
import ExpertAssistance from 'components/blocks/states/ExpertAssistance';
import HearFromSection from 'components/blocks/states/HearFromSection';
import RealWorldSection from 'components/blocks/states/RealWorldSection';
import ScienceTestSection from 'components/blocks/states/ScienceTestSection';

import tornadoSimulation from '../../../../stile-shared/assets/videos/tornado_sim_example.mp4';

export { Head } from 'templates/2024/Head';

const MississippiTopSection = styled.div`
  background: linear-gradient(to bottom, var(--stile-green-2), var(--stile-green-3));
  .content {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: auto auto auto;
    align-items: center;
    justify-items: center;
    padding: 40px 60px 0;
    max-width: 1160px;
    margin: 0 auto;
    gap: 10px;
    grid-template-rows: auto auto;
    grid-template-areas:
      'hero'
      'subtext';
  }

  .hero-container {
    display: grid;
    grid-template-rows: 1fr auto;
    width: 100%;
    max-width: 1160px;
    grid-area: hero;
    grid-column: 1 / -1;
    margin: 0 auto;

    /* Desktop: h1 and hero-media both in row 1 (overlapped) */
    @media (min-width: 769px) {
      h1 {
        grid-row: 1;
        grid-column: 1;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--white);
        text-align: center;
        margin: 0 auto;
        pointer-events: none;
        padding: 50px 150px 50px;
      }
    }

    /* Mobile: h1 in row 1, hero-media in row 2 (stacked) */
    @media (max-width: 768px) {
      margin: 0 0 32px 0;

      h1 {
        grid-row: 1;
        grid-column: 1;
        color: var(--white);
        text-align: center;
        margin: 0 auto;
        max-width: 100%;
        font-size: 32px;
        line-height: 40px;
        padding: 0 0 16px 0;
      }
    }
  }

  .hero-media {
    aspect-ratio: 1160/653;
    border-radius: 32px;
    box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
      0px 5px 11px 0px rgba(0, 0, 0, 0.07);
    width: 100%;

    /* Desktop: hero-media in row 1 (same as h1) */
    @media (min-width: 769px) {
      grid-row: 1;
      grid-column: 1;
    }

    /* Mobile: hero-media in row 2 (below h1) */
    @media (max-width: 768px) {
      grid-row: 2;
      grid-column: 1;
    }

    .gatsby-image-wrapper {
      border-radius: 32px;
      height: 100%;
      width: 100%;
    }

    @media (max-width: 768px) {
      border-radius: 16px !important;
      box-shadow: 0px 12.603px 7.621px 0px rgba(0, 0, 0, 0.04),
        0px 5.569px 5.569px 0px rgba(0, 0, 0, 0.06), 0px 1.466px 3.224px 0px rgba(0, 0, 0, 0.07);

      .gatsby-image-wrapper {
        border-radius: 16px;
      }
    }
  }

  .subtext {
    color: var(--white);
    text-align: center;
    font-size: 21px !important;
    font-style: normal !important;
    font-weight: 600 !important;
    line-height: 26px !important;

    /* Desktop */
    @media (min-width: 769px) {
      grid-area: subtext;
      grid-column: 3 / 11; /* span middle 8 of 12 columns */
      padding-bottom: 40px;
      padding-top: 22px;
      margin: 0;
    }

    /* Mobile */
    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      grid-column: 1 / -1;
      padding-bottom: 40px;
      margin: 0;
    }
  }

  @media (max-width: 768px) {
    .content {
      padding: 40px 40px 0;
      grid-template-rows: auto auto;
      grid-template-areas:
        'hero'
        'subtext';
    }

    .hero-container {
      grid-area: hero;
      grid-column: 1 / -1;
    }

    .subtext {
      grid-area: subtext;
      grid-column: 1 / -1;
    }
  }
`;

const MississippiPageLayout = styled(PageLayout)`
  .header-wrapper {
    @media (max-width: 768px) {
      background-color: var(--stile-green-2);
    }
  }
`;

const ReviewsComponent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  .splide {
    &:nth-child(2) {
      .splide__track {
        padding-bottom: 72px;
        @media (max-width: 768px) {
          padding-bottom: 60px;
        }
      }
    }
  }
  .hearts,
  .hearts-container {
    position: relative;
    overflow: visible;
    width: 100%;
    max-width: 1160px;
    @media (max-width: 1220px) {
      padding: 0 60px;
    }
    @media (max-width: 768px) {
      padding: 0 25px;
    }
    &:last-child {
      div {
        &:last-child {
          right: 25px;
          top: -30px;
        }
      }
    }
  }
  .hearts {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    h6 {
      text-align: center;
      color: var(--stile-grey-6);
      margin: 0 20px 40px;
      @media (max-width: 768px) {
        margin-top: 60px;
      }
    }
  }
  .quote {
    font-size: 14px !important;
    font-weight: 600 !important;
    line-height: 21px !important;

    @media (max-width: 768px) {
      line-height: 18px !important;
    }
  }
`;

const MississippiExpertAssistance = styled.div`
  background-color: var(--stile-grey-1);

  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

const LazyVideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 32px;
  overflow: hidden;
`;

const PlaceholderImage = styled.div<{ loaded: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ loaded }) => (loaded ? 0 : 1)};
  transition: opacity 0.5s ease-in-out;
  z-index: 2;

  .gatsby-image-wrapper {
    border-radius: 32px;
    height: 100%;
    width: 100%;
  }
`;

const VideoElement = styled.video<{ loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 32px;
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const reviews = [
  {
    name: 'Elizabeth',
    school: 'Lake Oswego',
    quote:
      'My favorite thing about Stile is that every feature in it is 100% customizable and editable by the teacher, so I can take the great platform that’s already there and make it work for my students.',
  },
  {
    name: 'Zach',
    school: 'Briggs Middle School',
    quote:
      'What I like about Stile is that it isn’t students just staring at a screen, it is driving a conversation... This is by far the most excited I have been about curriculum.',
  },
  {
    name: 'Tavin',
    school: 'Armand Larive Middle School',
    quote:
      "I think that out of all the curricula I've used, this one is very tech-savvy, and I believe students will be more engaged.",
  },
  {
    name: 'McKenzie',
    school: 'Siuslaw Middle School',
    quote:
      "I don't have to make it engaging because it’s already engaging. It's just so editable, but it doesn't have to be. It's the perfect happy medium.",
  },
  {
    name: 'Lex',
    school: 'Cheldelin Middle school',
    quote: 'Stile has the best customer service I’ve ever had in any part of my life.',
  },
  {
    name: 'Jill',
    school: 'Peak Charter Academy',
    quote: 'I wish I had this when I was a first year teacher.',
  },
  {
    name: 'Delfino',
    school: 'Hermiston School District',
    quote:
      'I think the biggest complement that I saw in Stile was how easy it was to customize our lessons and make them specific to our region. The kids will be like, was this written for us?',
  },
  {
    name: 'Chelsea',
    school: 'North Marion Middle School',
    quote:
      "The combination of the quality of the curriculum and the support that you get from the team is unlike any other curriculum that I've worked with.",
  },
  {
    name: 'Bettina',
    school: 'German International School',
    quote: "It engages kids in a way that a normal book wouldn't, or that I can't on my own.",
  },
  {
    name: 'Brittany',
    school: 'Mooresville Graded School District',
    quote: 'I got the highest scores in the district! Thank you Stile!!',
  },
];

// Performant video component with lazy loading and placeholder
const LazyTornadoVideo: React.FC = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing after visibility is triggered
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  return (
    <LazyVideoContainer ref={containerRef}>
      {/* Placeholder image */}
      <PlaceholderImage loaded={videoLoaded}>
        <StaticImage
          src="../../../../stile-shared/assets/images/states/mississippi/slider-1.jpg"
          alt="Tornado and severe weather phenomena"
          placeholder="blurred"
          loading="eager"
        />
      </PlaceholderImage>

      {/* Video element - only load when visible */}
      {isVisible && (
        <VideoElement
          ref={videoRef}
          loaded={videoLoaded}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={handleVideoLoad}
          onCanPlayThrough={handleVideoLoad}
        >
          <source src={tornadoSimulation} type="video/mp4" />
        </VideoElement>
      )}
    </LazyVideoContainer>
  );
};

function TwentyTwentyFourMississippiPage(props: PageProps) {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return (
    <MississippiPageLayout {...props}>
      <MississippiTopSection>
        <div className="content">
          <div className="hero-container">
            <h1>Built for the new Mississippi standards</h1>
            <div className="hero-media">
              <StaticImage
                src="../../../../stile-shared/assets/images/states/mississippi/header-hero-landscape.jpg"
                alt="Aerial view of the Mississippi River winding through sandbars at dusk"
                placeholder="none"
                loading="eager"
              />
            </div>
          </div>
          <p className="subtext">
            Explore our full core middle school science curriculum, thoughtfully aligned to the new
            state standards. Each lesson is ready to teach and customizable, giving new teachers the
            support they need, and veteran teachers the flexibility they deserve.
          </p>
        </div>
        <ReviewsComponent className="reviews-wide">
          <div className="hearts-container">
            <Hearts />
          </div>
          <ReviewsSlider reviews={reviews} />
        </ReviewsComponent>
      </MississippiTopSection>

      <AlignedSection
        heading="Fully aligned and completely customizable"
        body={
          <>
            <p>
              There’s ‘alignment.’ And then there’s actual alignment. Thanks to Stile’s powerful and
              customizable platform, we took the very best of our existing middle school science
              curriculum and blended it with brand-new lessons tailored for Mississippi. The result
              is a seamless, structured, and sequenced Middle School Science Curriculum for the
              state.
            </p>
            <p>Want to know more? Request a copy of our teacher guide to the new standards.</p>
          </>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/states/mississippi/aligned.jpg"
            alt="A person in a classroom, performing experiments"
          />
        }
        buttons={[
          <Button
            key={0}
            href="/states/mississippi-teacher-guide"
            label="Request a teacher guide"
            className="--alignment"
          />,
          <Button
            key={1}
            href="/states/mississippi-alignment"
            label="View Stile's alignment to Mississippi's standards"
            className="--alignment"
          />,
        ]}
      />

      <RealWorldSection
        stackVertical={true}
        heading="Real-world phenomena, from the coastal plains to the delta"
        subheading="Many of Stile’s phenomena are drawn from wonders all around 
        the world. After all, it’s important that students grow into global citizens. 
        But there’s nothing more fascinating than investigating phenomena right where you live. 
        Stile’s curriculum for Mississippi offers a balanced blend of local and global phenomena."
        tabs={[
          'The power of tropical storms',
          'Little Grand Canyon',
          'From patients to nurses',
          'Mississippi River nutrients',
        ]}
        headings={[
          'What in the whirlwind?',
          'Neshoba County sandstone',
          'From sickle-cell patients to nurses',
          'Why do some bodies of water turn green?',
        ]}
        paragraphs={[
          "In our Earth Systems unit, students research and use models to explain the awesome power of tropical storms. They'll use interactive maps to explore where and how tornadoes form and why they can be so darn tough to predict, with dire consequences for those in their way.",
          "In our Active Earth unit, students will uncover the physical processes involved in forming Mississippi's famed soil. Students look at the example of the Little Grand Canyon in Neshoba County and learn about the processes behind how the Pearl River helped shape it.",
          "In the opening lesson of our Cells unit, students hear the story of Erin and Logan Dorsey, two young women from Madison County born with sickle-cell disease. After receiving bone marrow transplants at Children's of Mississippi, they were recently hired as Registered Nurses at the same hospital. Their story serves as the vehicle for the unit's guiding question: “Would you donate a part of your body to save a life?”",
          'Students will tackle some of the complex issues of the Mississippi River in the Hydrosphere unit. What nutrients does it carry, and how can those nutrients both nurture life and harm it? In the Movement of Water lesson, students explore how nutrient runoff from as far away as Montana can travel along the Mississippi River and fuel the creation of the largest ecological “dead zone” in the United States, in the Gulf of Mexico.',
        ]}
        images={[
          <LazyTornadoVideo key={0} />,
          <StaticImage
            key={1}
            src="../../../../stile-shared/assets/images/states/mississippi/slider-2.jpg"
            alt="Eroded sandstone formations at Mississippi’s ‘Little Grand Canyon’"
          />,
          <StaticImage
            key={2}
            src="../../../../stile-shared/assets/images/states/mississippi/slider-3.jpg"
            alt="Two nurses in a hospital, smiling and standing back-to-back"
          />,
          <StaticImage
            key={3}
            src="../../../../stile-shared/assets/images/states/mississippi/slider-4.jpg"
            alt="Map of green algal bloom near a river delta along the coast"
          />,
        ]}
      />

      <ScienceTestSection
        heading="Prepare your students for the Mississippi Science Test"
        subHeading="The Mississippi 8th grade science test aims to measure students' 
        science knowledge and critical thinking ability as part of the Mississippi Academic 
        Assessment Program (MAAP). Stile’s curriculum precisely scaffolds both of these, 
        providing students with plenty of opportunities to practice and excel on the assessment."
        leftHeading="MAAP Test"
        leftContent={
          <>
            <p>
              The diagram shows some of the genetic materials involved in producing a cellular
              product.
            </p>
            <p>Record the numbers of the labels in the boxes to best complete the diagram.</p>
            <StaticImage
              src="../../../../stile-shared/assets/images/states/mississippi/mississippi-science-test.jpg"
              alt="Example question from the Mississippi Science Test"
            />
          </>
        }
        rightHeading="Stile MAAP Practice Test"
        rightContent={
          <>
            <p>
              The diagram below shows the organisation of genetic material within a cell, from the
              whole cell down to the instructions that affect traits.
            </p>
            <p>
              Drag and drop the labels into the boxes to correctly complete the diagram. Each label
              is used only once.
            </p>
            <StaticImage
              src="../../../../stile-shared/assets/images/states/mississippi/stile-science-test.jpg"
              alt="Example question from the Stile Science Test"
            />
          </>
        }
      />

      <HearFromSection
        heading="Hear from other educators"
        subheading="North Carolina released its own set of new standards in 2023. Stile has helped Haywood County teachers transition to the new NC standards while saving hours of prep time and boosting ELA skills along the way."
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/states/mississippi/video.png"
            alt="thumbnail from Stile YouTube video, about implementing Stile in Haywood County, NC"
          />
        }
        youtubeUrl="https://www.youtube.com/watch?v=sJ3Xnio77uk"
        button={<Button href="/why-choose-stile/case-studies" label="Hear more success stories" />}
      />

      <MississippiExpertAssistance>
        <ExpertAssistance
          heading="Pilot Stile with a team of experts behind you"
          copy="Stile is all about empowering teachers with the best support. Stile's support team is led by seasoned educators with extensive experience in teaching, administration, and instructional coaching. Together, teachers are supported (and lessons are supercharged!)."
          href="/why-choose-stile/oregon-pilot"
          expertImage={
            <StaticImage
              src="../../../../stile-shared/assets/images/states/mississippi/stile-support.jpg"
              alt="Stile support team member talk with teachers"
              quality={100}
              height={1250}
            />
          }
        />
      </MississippiExpertAssistance>
    </MississippiPageLayout>
  );
}

export default TwentyTwentyFourMississippiPage;
